import cn from 'classnames';
import React, { useEffect, useRef } from 'react';
import { OptionList } from 'src/components/card-widget/components/option-list';
import { PaymentOptionSummary } from 'src/components/card-widget/components/payment-option-summary';
import { Skeleton } from 'src/components/card-widget/components/skeleton';
import { LearnMore } from 'src/components/learn-more';
import { Portal } from 'src/components/portal';
import { ShadowView } from 'src/components/shadow-view';
import { ACTIONS, WIDGET_INITIALIZED } from 'src/constants/common';
import { COUNTRY_NAME_BY_CURRENCY_MAP } from 'src/constants/country-name-by-currency-map';
import {
  AppProvider,
  type LearnMoreType,
  type MerchantProps,
  useAppContext,
} from 'src/context/store/app-provider';
import { useAnalytics } from 'src/hooks/use-analytics';
import { useCollectWidgetContext } from 'src/hooks/use-collect-widget-context';
import { useOnExperimentsReady } from 'src/hooks/use-on-experiments-ready';
import { useCardDecision } from 'src/hooks/use-widget-decisions';
import { mobileBridge } from 'src/services/mobile-bridge';
import { SegmentEventType } from 'src/types/segment';
import { dataDog } from 'src/utils/datadog/data-dog';
import { delayDecorator } from 'src/utils/delay-decorator';
import { getLearnMoreLink } from 'src/utils/get-learn-more-link';
import { isMobileAppContext } from 'src/utils/is-mobile-app-context';
import { renderToDecorator } from 'src/utils/render-to-decorator';
import { setupDatadogForWidgets } from 'src/utils/setup-datadog-for-widgets';

import styles from './styles.css';

export const CardWidget = () => {
  const {
    isRtl,
    lang,
    installmentsCount,
    offers,
    loading,
    publicKey,
    merchantCode,
    currency,
    priceRaw,
    formattedCurrency,
    shouldInheritBg,
    dispatch,
  } = useAppContext();

  useOnExperimentsReady(setupDatadogForWidgets);

  const widgetContext = useCollectWidgetContext();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new ResizeObserver(([entry]) => {
      const el = entry.target as HTMLElement;
      mobileBridge.sendDimensions({
        width: el.offsetWidth,
        height: el.offsetHeight,
      });
    });

    if (containerRef.current) observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, []);

  const logAnalyticEvent = useAnalytics();

  const cardDecision = useCardDecision();
  const { widgetType } = cardDecision;

  const hasOffers = !!offers.installments?.length;

  const openLearnMorePage = () => {
    mobileBridge.openPage(
      getLearnMoreLink({
        price: priceRaw,
        currency: formattedCurrency,
        lang,
        ...(hasOffers && {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          public_key: publicKey,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          merchant_code: merchantCode,
        }),
      }),
      widgetContext
    );
  };

  const openLearnMoreDialog = (type: LearnMoreType) => {
    dispatch({
      type: ACTIONS.SHOW_LEARN_MORE,
      payload: { [type]: true },
    });
  };

  const openLearnMore = (type: LearnMoreType) => {
    const action = isMobileAppContext ? openLearnMorePage : openLearnMoreDialog;
    action(type);

    logAnalyticEvent({
      event: SegmentEventType.LEARN_MORE_CLICKED,
      publicKey: publicKey ? 'yes' : 'no',
      merchantIntegrationType: 'snippetAndPopup',
      planSelected: installmentsCount,
      snippetType: [3, 4].includes(installmentsCount)
        ? 'fullInformation'
        : 'notFullInformation',
      merchantCountry: COUNTRY_NAME_BY_CURRENCY_MAP[currency],
      typeWidget: 'card',
    });
  };

  const handleLearnMoreClick = () => {
    openLearnMore(cardDecision.learnMore);
  };

  return (
    <ShadowView shouldInheritColor shouldInheritBg={shouldInheritBg}>
      <>
        <div
          ref={containerRef}
          dir={isRtl ? 'rtl' : 'ltr'}
          className={cn(
            styles.container,
            shouldInheritBg ? null : styles.bgWhite
          )}
          onClick={handleLearnMoreClick}
          data-test="card-widget"
        >
          {loading ? (
            <Skeleton />
          ) : (
            <>
              <PaymentOptionSummary widgetType={widgetType} />
              <OptionList />
            </>
          )}
        </div>
        <Portal to="body">
          <LearnMore />
        </Portal>
      </>
    </ShadowView>
  );
};

declare global {
  interface Window {
    TabbyCard: (props: { selector: string } & MerchantProps) => void;
  }
}

window.TabbyCard = ({ selector, ...props }) => {
  dataDog.info('initialization', WIDGET_INITIALIZED, {
    constructorName: 'TabbyCard',
    ...props,
  });

  delayDecorator(() => {
    renderToDecorator(
      selector,
      <AppProvider {...props}>
        <CardWidget />
      </AppProvider>
    );
  });
};
